import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Overlay from 'react-overlays/es/Overlay';
import memoizeOne from 'memoize-one';
import getContainer from 'react-overlays/es/utils/getContainer';
import noop from 'lodash/noop';
import objectValues from '@adsk/bim360-matrix-react-helpers/es/objectValues';
import clickOutside from '@adsk/bim360-matrix-react-helpers/es/clickOutsideHOC';
import { Places, Themes, Sizes } from './helpers/consts';
import OverlayInnerChild from './OverlayInnerChild';

import './_Tooltip.scss';

const memoGetContainer = memoizeOne(getContainer);
const OverlayInnerChildWrapper = clickOutside(OverlayInnerChild);

/**
 * @class OverlayWrapper - the class that wraps the react-overlay component for Tooltip elements
 **/
class OverlayWrapper extends Component {
  constructor(props) {
    super(props);
    this.state = {
      arrowStyle: {},
      container: null
    };

    this.getPopperConfig = this.getPopperConfig.bind(this);
    this.getOverlay = this.getOverlay.bind(this);
  }

  componentDidMount() {
    this.setState({
      container: memoGetContainer(this.props.container)
    });
  }

  componentDidUpdate() {
    // Update the tooltip overlay position if needed.
    // E.g. Should ensure overlay stick to a correct position once the component position changed.
    this.scheduleUpdate && this.scheduleUpdate();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.container !== this.props.container || !this.state.container) {
      this.setState({
        container: memoGetContainer(nextProps.container)
      });
    }
  }

  /**
   * get the config data to be sent to the popper object from the Overlay component
   */

  getPopperConfig() {
    return {
      modifiers: {
        preventOverflow: {
          boundariesElement: this.state.container || 'viewport'
        }
      }
    };
  }

  getOverlay() {
    // eslint-disable-next-line no-unused-vars
    const { place, style, getTargetProps, overlayRef, onHide, ...rest } = this.props;
    const innerChildRef = ref => node => {
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref !== null) {
        ref.current = node;
      }
      if (typeof overlayRef === 'function') {
        overlayRef(node);
      } else if (overlayRef) {
        overlayRef.current = node;
      }
    };
    return (
      <Overlay
        key={'__$$OverlayWrapper__key'}
        show={this.props.show}
        container={this.state.container}
        target={this}
        placement={place}
        containerPadding={this.props.containerPadding}
        flip={true}
        popperConfig={this.getPopperConfig()}
      >
        {({ props, arrowProps, placement, scheduleUpdate }) => {
          this.scheduleUpdate = scheduleUpdate;
          return (
            <OverlayInnerChildWrapper
              {...rest}
              target={this}
              overlayRef={innerChildRef(props.ref)}
              arrowProps={arrowProps}
              place={placement}
              style={{ ...style, ...props.style }}
              rootClose={this.props.rootClose}
              handleClickOutside={onHide}
            />
          );
        }}
      </Overlay>
    );
  }

  render() {
    const overlay = this.getOverlay();
    return (
      <React.Fragment>
        {this.props.children instanceof Function ? (
          this.props.children({
            getContainerProps: ({ className = '', children = [], ...args } = {}) => {
              const { className: baseClass, ...targetProps } = this.props.getTargetProps(args);
              return {
                ...args,
                className: `${baseClass} ${className}`,
                children: Array.isArray(children)
                  ? children.concat(overlay)
                  : [<React.Fragment key={'children'}>{children}</React.Fragment>, overlay],
                ...targetProps
              };
            }
          })
        ) : (
          <div {...this.props.getTargetProps()}>
            {this.props.children}
            {overlay}
          </div>
        )}
      </React.Fragment>
    );
  }
}

OverlayWrapper.propTypes = {
  /**
   * The content of the element to which the tooltip is applied
   */
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node, PropTypes.func])
    .isRequired,
  /**
   * Whether or not show the tooltip
   */
  show: PropTypes.bool.isRequired,
  /**
   * className to add to the tooltip container
   */
  tooltipClassName: PropTypes.string,
  /**
   * style to add to the tooltip container
   */
  popoverStyle: PropTypes.object,
  /**
   * style to add to the layout container wrapping the children and the tooltip
   */
  style: PropTypes.object,
  /**
   * A Node, Component instance, or function that returns either. The `container` will have the Tooltip appended to it.
   */
  container: PropTypes.oneOfType([PropTypes.object, PropTypes.func]),
  /**
   * function getter props for trigger functions
   */
  getTargetProps: PropTypes.func,
  /**
   * Tooltip place
   */
  place: PropTypes.oneOf(Object.values(Places)),
  /**
   * Minimum spacing in pixels between container border and component border.
   */
  containerPadding: PropTypes.number,
  /**
   * Specify whether the overlay should trigger `onHide` when the user clicks outside the overlay
   */
  rootClose: PropTypes.bool,
  /**r
   * Triggered when user clicks outside the popover if rootClose=true
   */
  onHide: PropTypes.func,
  /**
   * Specify whether the overlay arrow color should match the tooltipClassName and popoverStyle background
   */
  matchArrowBackground: PropTypes.bool,
  /**
   * The coloring scheme of the overlay
   */
  theme: PropTypes.oneOf(objectValues(Themes)),
  /**
   * The size scheme of the tooltip
   */
  size: PropTypes.oneOf(objectValues(Sizes)),
  /**
   * whether to not have the arrow - show and default to true
   */
  showArrow: PropTypes.bool,
  /**
   * Ref for the overlay container
   */
  overlayRef: PropTypes.oneOfType([PropTypes.object, PropTypes.func])
};

OverlayWrapper.defaultProps = {
  show: false,
  place: Places.TOP,
  onHide: noop,
  theme: Themes.LIGHT,
  size: Sizes.NORMAL,
  showArrow: true,
  rootClose: true
};

export default OverlayWrapper;
